import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CalendarDays, CalendarHeart, FileText, FileSignature, Users, ClipboardList, Contact, ReceiptText, ChevronLeft, ShieldCheck, Stethoscope, ListTodo, Send, KeyRound, Megaphone, Mail, Link2, Globe, Video } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminHeader from "@/components/AdminHeader";

const TOP_TILE = { to: "/calendar", icon: CalendarDays, title: "Calendar", desc: "Events, bookings & rosters", testid: "admin-tile-calendar" };

const SECTIONS = [
  {
    title: "Clients", testid: "admin-section-clients",
    tiles: [
      { to: "/admin/contacts", icon: Contact, title: "Client contacts", desc: "Address book of clients & their people", testid: "admin-tile-contacts" },
      { to: "/admin/invoices", icon: ReceiptText, title: "Quotes & invoices", desc: "Build, send, chase & track payments", testid: "admin-tile-invoices" },
      { to: "/admin/jobs", icon: ListTodo, title: "To-Do", desc: "Invoices to raise, chase-ups & call follow-ups", testid: "admin-tile-jobs" },
      { to: "/admin/emails", icon: Mail, title: "Emails", desc: "Your inbox built in — read & reply without leaving", testid: "admin-tile-emails" },
    ],
  },
  {
    title: "Staff", testid: "admin-section-staff",
    tiles: [
      { to: "/admin/staff", icon: ClipboardList, title: "Rota & staff portal", desc: "Shifts, wages, availability & profiles", testid: "admin-tile-staff" },
      { to: "/admin/broadcast", icon: Send, title: "Send shift to staff", desc: "Email or WhatsApp a shift out — all staff or picked", testid: "admin-tile-broadcast" },
      { action: "logins", icon: Users, title: "Staff management", desc: "Logins, roles, grades, invites & passwords", testid: "admin-tile-staff-management" },
      { to: "/admin/website", icon: Globe, title: "Website", desc: "Edit the public website — text, photos, reviews & pages", testid: "admin-tile-website" },
    ],
  },
  {
    title: "Reporting & admin", testid: "admin-section-reporting",
    tiles: [
      { to: "/reports/organizer", icon: FileText, title: "End-of-day report", desc: "Organiser reports & casualty data", testid: "admin-tile-reports" },
      { to: "/admin/proposals", icon: FileSignature, title: "Proposals", desc: "Big jobs — proposal, schedule, pricing & terms, accepted online", testid: "admin-tile-proposals" },
      { to: "/admin/calls", icon: Video, title: "Video calls", desc: "Schedule a call, send a link, guests join from phone or laptop", testid: "admin-tile-calls" },
      { to: "/admin/medical-plan", icon: Stethoscope, title: "Medical Plan", desc: "Build & send event medical plans", testid: "admin-tile-medical-plan" },
      { to: "/admin/marketing", icon: Megaphone, title: "Marketing", desc: "Social calendar, campaigns, complaints & retention", testid: "admin-tile-marketing" },
      { action: "control-pw", icon: KeyRound, title: "Control room password", desc: "Reset the password the control room screen uses", testid: "admin-tile-control-password" },
    ],
  },
];

export default function AdminHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [controlPwOpen, setControlPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetting, setResetting] = useState(false);

  const resetControlPw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("The two passwords don't match");
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.post("/control/admin-reset", { password: newPw });
      toast.success(data.message);
      setControlPwOpen(false);
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    api.get("/admin/monthly-stats").then((r) => setStats(r.data.months)).catch(() => {});
  }, []);

  const maxEvents = stats ? Math.max(...stats.map((m) => m.events), 1) : 1;
  const maxTurnover = stats ? Math.max(...stats.map((m) => m.turnover), 1) : 1;

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="admin-hub">
      <AdminHeader title="Admin" testid="admin-hub-title" backTo="/dashboard" backTestid="admin-hub-back" eyebrow="WEMS Portal"
        actions={(
          <>
            <Link to="/admin/my-calendar" data-testid="admin-bar-my-calendar"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold transition-colors press">
              <CalendarHeart className="h-4 w-4" /> My calendar
            </Link>
            <Link to="/admin/links" data-testid="admin-bar-useful-links"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-300 text-[#0F2547] text-xs font-bold hover:border-[#005EB8] hover:text-[#005EB8] transition-colors press">
              <Link2 className="h-4 w-4" /> Useful links
            </Link>
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="admin-hub-user-name">{user?.name}</span>
          </>
        )} />

      <main className="max-w-5xl mx-auto p-4 sm:p-8">
        {(() => {
          const tileInner = (t) => (
            <>
              <div className="h-9 w-9 rounded-lg bg-[#F4F7FA] border border-slate-200 grid place-items-center mb-2.5 group-hover:bg-[#005EB8] transition-colors">
                <t.icon className="h-[18px] w-[18px] text-[#005EB8] group-hover:text-white transition-colors" />
              </div>
              <p className="font-bold text-[#0F2547] text-sm">{t.title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-snug">{t.desc}</p>
            </>
          );
          const tileCls = "card-lift press text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-[#005EB8] group";
          const renderTile = (t) => {
            if (t.action === "logins") {
              return <button key={t.testid} onClick={() => navigate("/admin/staff?tab=logins")} data-testid={t.testid} className={tileCls}>{tileInner(t)}</button>;
            }
            if (t.action === "control-pw") {
              return <button key={t.testid} onClick={() => setControlPwOpen(true)} data-testid={t.testid} className={tileCls}>{tileInner(t)}</button>;
            }
            return <Link key={t.testid} to={t.to} data-testid={t.testid} className={tileCls}>{tileInner(t)}</Link>;
          };
          return (
            <>
              <Link to="/admin/send-info" data-testid="admin-tile-send-info"
                className="flex items-center gap-4 bg-white rounded-xl border-2 border-[#00A499]/40 p-4 hover:border-[#00A499] transition-colors group mb-3">
                <div className="h-10 w-10 rounded-lg bg-[#00A499]/10 grid place-items-center shrink-0">
                  <Send className="h-5 w-5 text-[#00A499]" />
                </div>
                <div>
                  <p className="font-heading font-bold text-[#0F2547] text-base">Send client information</p>
                  <p className="text-xs text-slate-500 mt-0.5">Email a client your packages &amp; prices or the people we trust page — and edit those pages</p>
                </div>
              </Link>
              <Link to={TOP_TILE.to} data-testid={TOP_TILE.testid}
                className="flex items-center gap-4 bg-[#0F2547] rounded-xl p-4 hover:bg-[#173766] transition-colors group">
                <div className="h-10 w-10 rounded-lg bg-white/10 grid place-items-center shrink-0">
                  <CalendarDays className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white text-base">{TOP_TILE.title}</p>
                  <p className="text-xs text-sky-200 mt-0.5">{TOP_TILE.desc}</p>
                </div>
              </Link>
              {SECTIONS.map((sec) => (
                <div key={sec.title} className="mt-8" data-testid={sec.testid}>
                  <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{sec.title}</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {sec.tiles.filter((t) => t.testid !== "admin-tile-emails" || user?.is_owner).map(renderTile)}
                  </div>
                </div>
              ))}
            </>
          );
        })()}

        {stats && (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5" data-testid="monthly-stats">
            <h2 className="font-heading text-lg font-bold text-[#0F2547] mb-1">Events & predicted turnover by month</h2>
            <p className="text-xs text-slate-500 mb-4">From the calendar — turnover is the total of client charges on each month's events.</p>
            <div className="space-y-2.5">
              {stats.map((m) => (
                <div key={m.month} className="flex items-center gap-3" data-testid={`month-row-${m.month}`}>
                  <span className="w-14 text-[11px] font-semibold text-slate-500 shrink-0">{m.label}</span>
                  <div className="flex-1 space-y-1">
                    <div className="h-3 rounded bg-slate-100 overflow-hidden">
                      <div className="h-full bg-[#005EB8] rounded transition-all" style={{ width: `${(m.events / maxEvents) * 100}%` }} />
                    </div>
                    <div className="h-3 rounded bg-slate-100 overflow-hidden">
                      <div className="h-full bg-[#007F3B] rounded transition-all" style={{ width: `${(m.turnover / maxTurnover) * 100}%` }} />
                    </div>
                  </div>
                  <span className="w-36 text-right text-[11px] font-semibold text-slate-600 shrink-0">
                    {m.events} event{m.events === 1 ? "" : "s"} · £{m.turnover.toFixed(0)} predicted
                    <span className="block text-[#007F3B]" data-testid={`month-received-${m.month}`}>£{(m.received || 0).toFixed(0)} received</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#005EB8]" /> Events</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#007F3B]" /> Turnover (client charges)</span>
            </div>
          </div>
        )}
      </main>

      <Dialog open={controlPwOpen} onOpenChange={setControlPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Reset control room password</DialogTitle>
          </DialogHeader>
          <form onSubmit={resetControlPw} className="space-y-4" data-testid="control-pw-form">
            <p className="text-sm text-slate-500">
              This changes the password used to unlock the control room screen. Anyone signed in there stays signed in;
              new sign-ins will need the new password.
            </p>
            <input type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="New control room password (min 6 characters)" data-testid="control-pw-new"
              className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input type="password" required minLength={6} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Type it again" data-testid="control-pw-confirm"
              className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <button type="submit" disabled={resetting} data-testid="control-pw-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-bold">
              {resetting ? "Saving…" : "Save new password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
